import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as api from './api';
import type { Adventure, TripGroup, UserIdentity } from '../types';

export const queryKeys = {
  metadata: (userId: string) => ['metadata', userId] as const,
  htmlExists: (s3Prefix: string) => ['htmlExists', s3Prefix] as const,
};

export interface MetadataResult {
  adventures: Adventure[];
  tripGroups: TripGroup[];
  subdomain: string;
  s3Prefix: string;
}

export function useMetadata(user: UserIdentity | null) {
  return useQuery({
    queryKey: queryKeys.metadata(user?.user_id ?? ''),
    queryFn: async (): Promise<MetadataResult> => {
      const meta = await api.getMetadata();
      if (!meta) throw new Error('Failed to load metadata');
      const adventures = Object.entries(meta.adventures ?? {})
        .map(([id, adv]) => ({ ...adv, id } as Adventure))
        .sort((a, b) => b.orderId - a.orderId);
      return {
        adventures,
        tripGroups: (meta as any).trip_groups ?? [],
        subdomain: (meta as any).subdomain ?? '',
        s3Prefix: (meta as any).s3_prefix ?? '',
      };
    },
    enabled: !!user,
  });
}

export function useHTMLExists(s3Prefix: string | undefined) {
  return useQuery({
    queryKey: queryKeys.htmlExists(s3Prefix ?? ''),
    queryFn: () => api.checkHTMLExists(s3Prefix!),
    enabled: !!s3Prefix,
    staleTime: 30_000,
  });
}

export function useInvalidateMetadata() {
  const queryClient = useQueryClient();
  return (userId: string) =>
    queryClient.invalidateQueries({ queryKey: queryKeys.metadata(userId) });
}
